import CmsAPI from '../../common/apis/CmsAPI';

class ResourceHelper {
  cancelJobFlag = false;
  stopPollingFlag = false;

  /**
   * Find a story or scene resource by it's label
   *
   * @param {object} storyOrScene
   * @param {string} resourceLabel
   * @returns `object` when found, else `undefined`
   */
  static getResourceByLabel(storyOrScene, resourceLabel) {
    if ('resources' in storyOrScene) {
      let resource;
      const labelResources = storyOrScene.resources.filter(obj => {
        return obj.label === resourceLabel;
      });
      if (labelResources.length > 1) {
        resource = labelResources.reduce((prev, current) => (prev.created > current.created ? prev : current));
      } else {
        resource = labelResources[0];
      }
      return resource;
    } else {
      return undefined;
    }
  }

  /**
   * Find all resources by label sorted by creation date
   * @param {*} storyOrScene 
   * @param {*} resourceType 
   * @returns 
   */
  static getAllResourcesByType(storyOrScene, resourceType) {
    if ('resources' in storyOrScene) {
      return storyOrScene.resources.filter(obj => {
        return obj.type === resourceType;
      })?.sort((a, b) => a.created - b.created);
    } else {
      return undefined;
    }
  }

  /**
   * Find a story or scene resource by it's url
   *
   * @param {object} storyOrScene
   * @param {string} resourceLabel
   * @returns `object` when found, else `undefined`
   */
  static getResourceByUrl(storyOrScene, resourceUrl) {
    if ('resources' in storyOrScene) {
      let resource = storyOrScene.resources.find(obj => {
        return obj.url === resourceUrl;
      });

      return resource;
    } else {
      return undefined;
    }
  }

  static async deleteResource(resourceId) {
    CmsAPI.deleteResource(resourceId);
  }

  static async updateResource(resourceId, resource) {
    CmsAPI.updateResource(resourceId, resource);
  }

  /**
   * Update multiple resources
   *
   * @param {array} resources Array of resource objects. Per object `id` is required, `label` and `properties` optional
   */
  static async updateResources(resources) {
    let payload = {
      resources: resources,
    };

    CmsAPI.updateResources(payload);
  }

  /**
   * Helper function to select/unselect thumbnails or other resources
   *
   * @param {string} select ID of the Resource you want to select
   * @param {string} unselect ID of the Resource you want to UNselect
   */
  static async selectResource(select, unselect = null) {
    let payload = [
      {
        id: select,
        properties: {
          selected: true,
        },
      },
    ];

    if (unselect) {
      payload.push({
        id: unselect,
        properties: {
          selected: false,
        },
      });
    }
    await ResourceHelper.updateResources(payload);
  }

  static async getJobDetails(jobId, onSuccess = null, onFail = null, tries = 0) {
    if (this.cancelJobFlag || this.stopPollingFlag) {
      this.cancelJobFlag = false;
      this.stopPollingFlag = false;
      return;
    } else {
      try {
        let jobDetails = await CmsAPI.getJobDetails(jobId);
        if (jobDetails.status === 'failed') {
          console.log('jobDetails status failed');
          onFail !== null && onFail(jobDetails);
          return;
        }

        // Retry on 404
        if (jobDetails.response && jobDetails.response.status === 404 && tries <= 100) {
          console.log(`404, probing again in ${3 * tries}s`);
          setTimeout(function() {
            ResourceHelper.getJobDetails(jobId, onSuccess, onFail, tries + 1);
          }, 3000 * tries);
          return;
        }

        // If job isn't done yet, try to get the job details again in 3 seconds, max 15 times
        if (jobDetails.outputs[0].url == null && tries <= 100) {
          console.log(`Not done yet, probing again in ${3 * tries}s`);
          setTimeout(function() {
            ResourceHelper.getJobDetails(jobId, onSuccess, onFail, tries + 1);
          }, 3000 * tries);
        }

        if ((jobDetails.outputs[0].label == "enhanced" || jobDetails.outputs[0].label == 'avatar') && jobDetails.status != 'succeeded' && tries <= 100) {
          console.log(`Not done yet, probing again in ${3 * tries}s`);
          setTimeout(function () {
            ResourceHelper.getJobDetails(jobId, onSuccess, onFail, tries + 1);
          }, 3000 * tries);
        }

        // Jub succeeded
        if (jobDetails.status == 'succeeded') {
          // Now we can show the actual thumbnail
          console.log('Job succeeded');

          if (onSuccess !== null) {
            onSuccess(jobId, jobDetails);
          }

          return jobDetails;
        }
      } catch (e) {
        console.log(e);
      }
    }
  }

  static async cancelJob(jobId) {
    const result = await CmsAPI.cancelJob(jobId);
    this.cancelJobFlag = true;
  }

  static async stopPolling() {
    this.stopPollingFlag = true;
  }

  /**
   * Check if a video needs to be processed before downloading it
   *
   * @param {object} video Video details object
   * returns false if it needs processing, else returns download URL
   */
  static downloadLink(video) {
    const hasSubtitles = 'subtitlesMeta' in video && video.subtitlesMeta.length > 0;

    // If this is a mobile video without subs, no processing is required, so download immediately
    if (video.source == 'mobile' && !hasSubtitles) {
      return video.video;
    }

    // If no recent changes to story, immediately download
    const hardsubs = ResourceHelper.getResourceByLabel(video, 'storyWithHardsubs');
    const trimmed = ResourceHelper.getResourceByLabel(video, 'trimmedStory');
    let lastDownloadDate = null;
    let link = null;

    if (hardsubs !== undefined) {
      lastDownloadDate = hardsubs.created;
      link = hardsubs.url;
    } else if (trimmed !== undefined) {
      lastDownloadDate = trimmed.created;
      link = trimmed.url;
    }

    if (lastDownloadDate !== null) {
      const lastUpdatedDate = Date.parse(video.updateDate) / 1000;

      console.log('lastUpdatedDate', lastUpdatedDate, 'lastDownloadDate', lastDownloadDate);

      // If story hasn't been updated after the last download, immediately download
      if (lastUpdatedDate <= lastDownloadDate) {
        return link;
      }
    }

    // Video needs processing
    return false;
  }
}

export default ResourceHelper;
